import React, { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { ApolloProvider } from 'react-apollo';
import { SnackbarProvider } from 'notistack';
import apolloClient from '../../lib/apolloClient';
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate.jsx';
import Card from '../../../../ui/canon/Card';
import Button from '../../../../ui/canon/Button';
import { useSuccessionTheme } from './SuccessionThemeProvider';
import { pctSymbol } from '../../utils/formatters';
import DocumentUploadModal from '../Vault/DocumentUploadModal';
import {
  CheckCircle2,
  Clock,
  FileText,
  ChevronRight,
  Mail,
  Download,
  ScrollText,
  AlertCircle,
  ArrowRight,
  Lock,
  FileDown,
  Upload,
  Sparkles,
  Users,
  UserCheck,
  PenLine,
  Shield,
} from 'lucide-react';

const REQUEST_CAT_PLAN_EMAIL = gql`
  mutation RequestCatPlanEmail {
    request_cat_plan_email {
      success
      message
    }
  }
`;

const START_CONTINUITY_BUILDER = gql`
  mutation StartContinuityBuilder {
    start_continuity_builder {
      success
      errors
    }
  }
`;

// --- Template URLs by affiliate ---
const TEMPLATE_BY_AFFILIATE = {
  RJA: {
    url: 'https://findbob-production.s3-us-west-2.amazonaws.com/rj/Template+Agreements/RJA+Agreements/Commitment+Agreement+Template+-+RJA.pdf',
    fileName: 'Commitment Agreement Template - RJA.pdf',
    label: 'Download Commitment Agreement Template',
  },
  ICD: {
    url: 'https://findbob-production.s3-us-west-2.amazonaws.com/rj/Template+Agreements/ICD+Agreements/Sample+Catastrophic+Plan+Template+(ICD).pdf',
    fileName: 'Sample Catastrophic Plan Template (ICD).pdf',
    label: 'Download Catastrophic Succession Plan Template',
  },
  default: {
    url: 'https://findbob-production.s3-us-west-2.amazonaws.com/resources/Business+Continuity+Agreement+Template+v1.1.docx',
    fileName: 'Business Continuity Agreement Template v1.1.docx',
    label: 'Download Agreement Template',
  },
};

// --- Status badge ---
const statusConfig = {
  complete:         { label: 'Complete',          className: 'bg-succession-success/10 text-succession-success border border-succession-success/20' },
  in_progress:      { label: 'In Progress',       className: 'bg-succession-info/10 text-succession-info border border-succession-info/20' },
  filed:            { label: 'Filed',             className: 'bg-succession-info/10 text-succession-info border border-succession-info/20' },
  pending_approval: { label: 'Awaiting Approval', className: 'bg-succession-warning/10 text-succession-warning border border-succession-warning/20' },
  approved:         { label: 'Approved',          className: 'bg-succession-success/10 text-succession-success border border-succession-success/20' },
  not_started:      { label: 'Not Started',       className: 'bg-succession-bg-secondary text-succession-text-muted border border-succession-border' },
};

const StatusBadge = ({ status }) => {
  const { label, className } = statusConfig[status] || statusConfig.not_started;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
      {label}
    </span>
  );
};

// --- Step circle (left rail) ---
const StepCircle = ({ number, status }) => {
  const isDone    = status === 'complete' || status === 'approved';
  const isActive  = status === 'in_progress' || status === 'filed' || status === 'pending_approval';

  if (isDone) return (
    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-succession-success/10 border-2 border-succession-success shrink-0">
      <CheckCircle2 className="h-5 w-5 text-succession-success" />
    </div>
  );
  if (isActive) return (
    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-succession-info/10 border-2 border-succession-info shrink-0">
      <Clock className="h-5 w-5 text-succession-info" />
    </div>
  );
  return (
    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-succession-bg-secondary border-2 border-succession-border shrink-0">
      <span className="text-sm font-semibold text-succession-text-muted">{number}</span>
    </div>
  );
};

// --- Card border accent by status ---
const cardBorderClass = (status) => {
  if (status === 'complete' || status === 'approved')    return 'border-succession-success/40';
  if (status === 'in_progress' || status === 'filed' || status === 'pending_approval') return 'border-succession-info/40';
  return 'border-succession-border';
};

// --- Step card (timeline layout) ---
const StepCard = ({ number, title, description, status, isLast, children, isOptional = false }) => (
  <div className="flex gap-4">
    {/* Left rail: circle + connector */}
    <div className="flex flex-col items-center shrink-0 w-10">
      <StepCircle number={number} status={status} />
      {!isLast && (
        <div className="w-px flex-1 min-h-6 mt-2 bg-succession-border" />
      )}
    </div>

    {/* Card */}
    <div className={`flex-1 mb-5 bg-succession-bg-primary border ${cardBorderClass(status)} rounded-xl shadow-succession-sm overflow-hidden`}>
      {/* Header */}
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-start justify-between gap-3 mb-1.5">
          <h3 className="text-sm font-semibold text-succession-text-primary leading-snug">{title}</h3>
          <div className="flex items-center gap-1.5 shrink-0">
            {isOptional && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-succession-bg-secondary text-succession-text-muted border border-succession-border">
                Optional
              </span>
            )}
            <StatusBadge status={status} />
          </div>
        </div>
        <p className="text-xs text-succession-text-secondary leading-relaxed">{description}</p>
        {isOptional && (
          <p className="text-xs italic text-succession-text-muted mt-1">
            You can skip this step and file your agreement directly — you can always add a partner later.
          </p>
        )}
      </div>

      {/* Action area */}
      {children && (
        <div className="border-t border-succession-border px-5 py-3.5">
          {children}
        </div>
      )}
    </div>
  </div>
);

const REGISTER_EXISTING_PARTNER = gql`
  mutation RegisterExistingPartner($name: String!, $email: String!, $phone: String) {
    register_existing_partner(name: $name, email: $email, phone: $phone) {
      success
      errors
    }
  }
`;

// Shared button styles — matches Step 1's action button exactly
const actionBtn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-succession-border text-succession-text-primary bg-succession-bg-secondary hover:bg-succession-hover-bg transition-theme duration-theme shrink-0';
const primaryBtn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover transition-theme duration-theme shrink-0 disabled:opacity-60';
const inputCls = 'w-full px-2.5 py-1.5 text-xs rounded-md border border-succession-border bg-succession-bg-secondary text-succession-text-primary placeholder:text-succession-text-muted focus:outline-none focus:ring-1 focus:ring-succession-primary transition-theme duration-theme';

// --- Step 2: find / register a partner ---
const Step2FindPartner = ({ cb, brandedName, searchOnly = false }) => {
  const existingName  = cb?.existing_partner_name  || '';
  const existingEmail = cb?.existing_partner_email || '';
  const existingPhone = cb?.existing_partner_phone || '';
  const hasRegistered = !!cb?.existing_partner_timestamp;
  const partnerComplete = cb?.partner_step_completed || (cb?.partners?.length || 0) > 0;
  const platformPartners = cb?.partners || [];

  const [mode, setMode]           = useState(hasRegistered ? 'registered' : 'choice');
  const [form, setForm]           = useState({ name: existingName, email: existingEmail, phone: existingPhone });
  const [formError, setFormError] = useState(null);

  const [registerPartner, { loading }] = useMutation(REGISTER_EXISTING_PARTNER, { client: apolloClient });

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError(null);
    if (!form.name.trim() || !form.email.trim()) { setFormError('Name and email are required.'); return; }
    registerPartner({ variables: { name: form.name, email: form.email, phone: form.phone || null } })
      .then(({ data }) => {
        const r = data?.register_existing_partner;
        if (r?.success) window.location.reload();
        else setFormError(r?.errors?.[0] || 'Something went wrong. Please try again.');
      })
      .catch(() => setFormError('Something went wrong. Please try again.'));
  };

  // ── Registered ───────────────────────────────────────────────────────────
  if (mode === 'registered' && hasRegistered) return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 min-w-0 text-xs">
          <UserCheck className="h-3.5 w-3.5 text-succession-success shrink-0" />
          <span className="font-medium text-succession-text-primary truncate">
            {existingName}
            <span className="font-normal text-succession-text-muted">
              {' · '}{existingEmail}{existingPhone ? ` · ${existingPhone}` : ''}
            </span>
          </span>
        </span>
        <button onClick={() => { setForm({ name: existingName, email: existingEmail, phone: existingPhone }); setMode('form'); }} className={actionBtn}>
          Update <ChevronRight className="h-3 w-3" />
        </button>
      </div>
      <p className="mt-2 pt-2 border-t border-succession-border text-xs text-succession-text-muted">
        <a href="/succession/search" className="text-succession-primary hover:text-succession-primary-hover underline underline-offset-2 transition-colors duration-theme">Search the network</a>
        {' '}to discover other potential partners.
      </p>
    </div>
  );

  // ── Registration form ────────────────────────────────────────────────────
  if (mode === 'form') return (
    <form onSubmit={handleSubmit} className="space-y-2.5">
      <p className="text-xs text-succession-text-muted">Provide your partner's contact details for your home office.</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-succession-text-muted mb-1">Name <span className="text-red-500">*</span></label>
          <input type="text" value={form.name} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, name: v })); }} placeholder="Full name" required className={inputCls} />
        </div>
        <div>
          <label className="block text-xs text-succession-text-muted mb-1">Email <span className="text-red-500">*</span></label>
          <input type="email" value={form.email} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, email: v })); }} placeholder="partner@example.com" required className={inputCls} />
        </div>
      </div>
      {formError && <p className="text-[11px] text-red-500">{formError}</p>}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={loading} className={primaryBtn}>
          <UserCheck className="h-3.5 w-3.5" />
          {loading ? 'Saving…' : 'Register Partner'}
        </button>
        <button type="button" onClick={() => { setFormError(null); setMode(hasRegistered ? 'registered' : 'choice'); }} className="text-xs text-succession-text-muted hover:text-succession-text-primary underline">
          Cancel
        </button>
      </div>
    </form>
  );

  // ── Complete via search ──────────────────────────────────────────────────
  if (partnerComplete && !hasRegistered) return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-1.5 text-xs text-succession-text-secondary">
        <CheckCircle2 className="h-3.5 w-3.5 text-succession-success shrink-0" />
        {platformPartners.length > 0 ? `Partner: ${platformPartners.join(', ')}` : 'Partner search completed.'}
      </span>
      <div className="flex items-center gap-2 shrink-0">
        <a href="/succession/search" className={actionBtn}>Search again <ChevronRight className="h-3 w-3" /></a>
        {!searchOnly && (
          <button onClick={() => setMode('form')} className="text-xs text-succession-text-muted hover:text-succession-text-primary underline">Add details</button>
        )}
      </div>
    </div>
  );

  // ── Choice (default) ─────────────────────────────────────────────────────
  return (
    <div className="space-y-2">
      {!searchOnly && (
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-xs text-succession-text-secondary">
            <UserCheck className="h-3.5 w-3.5 text-succession-text-muted shrink-0" />
            I already have a partner
          </span>
          <button onClick={() => setMode('form')} className={actionBtn}>
            Register details <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 text-xs text-succession-text-secondary">
          <Users className="h-3.5 w-3.5 text-succession-text-muted shrink-0" />
          Find a partner on {brandedName || 'FindBob'}
        </span>
        <a href="/succession/search" className={actionBtn}>
          Search network <ChevronRight className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
};

// --- Step 1 extended action: plan CTA + document downloads ---
const Step1PlanAction = ({ planStatus, planLabel, templateInfo, upp }) => {
  const completion = upp?.completion_percentage || 0;
  const showDocSection = !!templateInfo;

  return (
    <div>
      {/* Primary CTA row */}
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 text-xs text-succession-text-muted">
          {planStatus === 'not_started' && <PenLine className="h-3.5 w-3.5 shrink-0" />}
          {planStatus === 'complete'    && <Sparkles className="h-3.5 w-3.5 text-succession-success shrink-0" />}
          {planStatus === 'not_started' ? 'Build Catastrophic Plan'
            : planStatus === 'in_progress' ? `${completion}${pctSymbol()} complete`
            : 'Your completed plan'}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {planStatus === 'complete' && (
            <a href="/succession/plans/catastrophic-plan-v1/complete?autoDownload=pdf" className={actionBtn}>
              <FileDown className="h-3 w-3" /> Download
            </a>
          )}
          <a href="/succession/plans/catastrophic-plan-v1" className={actionBtn}>
            {planStatus === 'not_started' ? 'Start Plan' : planStatus === 'complete' ? 'Review Plan' : 'Continue Plan'}
            <ChevronRight className="h-3 w-3" />
          </a>
        </div>
      </div>

      {/* Progress bar — in_progress only, sits flush below the CTA row */}
      {planStatus === 'in_progress' && (
        <div className="mt-2 h-0.5 rounded-full bg-succession-border overflow-hidden">
          <div
            className="h-full rounded-full bg-succession-primary transition-[width] duration-700 ease-out"
            style={{ width: `${completion}%` }}
          />
        </div>
      )}

      {/* Document downloads — only when template exists or plan is downloadable */}
      {showDocSection && (
        <div className="mt-2.5 space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-succession-border/40" />
            <span className="text-[10px] italic text-succession-text-muted/50">or</span>
            <div className="flex-1 h-px bg-succession-border/40" />
          </div>
          {templateInfo && (
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5 text-xs text-succession-text-secondary">
                <FileText className="h-3.5 w-3.5 text-succession-text-muted shrink-0" />
                {templateInfo.affiliateLabel}
              </span>
              <a href={templateInfo.url} target="_blank" rel="noopener noreferrer" download={templateInfo.fileName} className={actionBtn}>
                <Download className="h-3 w-3" /> Download
              </a>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

// --- Step 3: upload only ---
const Step3FileAgreement = ({ agreementStatus, onUpload, templateInfo = null }) => {
  const templateDownload = templateInfo ? (
    <div className="mt-2 pt-2 border-t border-succession-border/70 flex items-center justify-between gap-3">
      <span className="flex items-center gap-1.5 text-xs text-succession-text-secondary">
        <FileText className="h-3.5 w-3.5 text-succession-text-muted shrink-0" />
        {templateInfo.affiliateLabel}
      </span>
      <a href={templateInfo.url} target="_blank" rel="noopener noreferrer" download={templateInfo.fileName} className={actionBtn}>
        <Download className="h-3 w-3" /> Download Template
      </a>
    </div>
  ) : null;

  if (agreementStatus === 'pending_approval') return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 text-xs text-succession-text-secondary">
          <Clock className="h-3.5 w-3.5 text-succession-warning shrink-0" />
          Agreement filed — awaiting approval from your firm.
        </span>
        <button onClick={onUpload} className={actionBtn}>
          <Upload className="h-3 w-3" /> Re-upload
        </button>
      </div>
      {templateDownload}
    </div>
  );
  if (agreementStatus === 'approved') return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 text-xs text-succession-success">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Agreement approved.
        </span>
        <button onClick={onUpload} className={actionBtn}>
          <Upload className="h-3 w-3" /> Update Agreement
        </button>
      </div>
      {templateDownload}
    </div>
  );
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <span />
        <button onClick={onUpload} className={primaryBtn}>
          <Upload className="h-3.5 w-3.5" /> Upload Agreement
        </button>
      </div>
      {templateDownload}
    </div>
  );
};

// --- Empty state (no ContinuityBuilder yet) ---
const PactBuilderEmptyState = ({ hasCatPlan, planBuilderEnabled = true, subtitle, searchOnly = false }) => {
  const partnerStepLabel = searchOnly
    ? 'Search for a succession partner'
    : 'Identify and register a succession partner';
  const [startContinuityBuilder, { loading }] = useMutation(START_CONTINUITY_BUILDER, { client: apolloClient });
  const [error, setError] = useState(null);

  const handleGetStarted = () => {
    setError(null);
    startContinuityBuilder()
      .then(({ data }) => {
        const result = data?.start_continuity_builder;
        if (result?.success) {
          window.location.reload();
        } else {
          setError(result?.errors?.[0] || 'Something went wrong. Please try again.');
        }
      })
      .catch(() => setError('Something went wrong. Please try again.'));
  };

  return (
    <SingleColumnTemplate
      title="Your Catastrophic Agreement"
      subtitle={subtitle || "File your catastrophic plan agreement with your home office."}
      columnClassName="max-w-2xl"
    >
      <Card className="p-8">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-14 h-14 bg-succession-primary/10 rounded-full flex items-center justify-center mb-4">
            <ScrollText className="w-7 h-7 text-succession-primary" />
          </div>
          <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
            File Your Catastrophic Plan Agreement
          </h3>
          <p className="text-sm text-succession-text-secondary max-w-md">
            This tool guides you through the steps to register your catastrophic succession
            agreement with your home office — from building your plan to uploading your signed
            agreement.
          </p>
        </div>

        <div className="space-y-3 mb-8">
          {(planBuilderEnabled
            ? [
              { step: '1', label: 'Create your catastrophic succession plan' },
              { step: '2', label: partnerStepLabel },
              { step: '3', label: 'Download, sign, and upload your agreement' },
              { step: '4', label: 'Receive confirmation once your home office approves' },
            ]
            : [
              { step: '1', label: partnerStepLabel },
              { step: '2', label: 'Download, sign, and upload your agreement' },
              { step: '3', label: 'Receive confirmation once your home office approves' },
            ]).map(({ step, label }) => (
            <div key={step} className="flex items-center gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-succession-bg-secondary border border-succession-border text-xs font-semibold text-succession-text-muted flex items-center justify-center">
                {step}
              </span>
              <span className="text-sm text-succession-text-secondary">{label}</span>
            </div>
          ))}
        </div>

        {hasCatPlan && (
          <div className="mb-6 px-4 py-3 bg-succession-success/10 border border-succession-success/20 rounded-lg">
            <p className="text-xs text-succession-success font-medium">
              You already have a catastrophic plan in progress. Getting started will pick up where you left off.
            </p>
          </div>
        )}

        {error && (
          <p className="mb-4 text-xs text-red-600">{error}</p>
        )}

        <Button
          variant="primary"
          onClick={handleGetStarted}
          disabled={loading}
          className="w-full justify-center"
        >
          {loading ? 'Setting up…' : 'Get Started'}
          {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>

        <div className="mt-6 flex gap-2.5 p-3 bg-succession-bg-secondary border border-succession-border rounded-lg">
          <AlertCircle className="w-4 h-4 text-succession-text-muted shrink-0 mt-0.5" />
          <p className="text-xs text-succession-text-muted leading-relaxed">
            <strong className="text-succession-text-secondary">Important:</strong> This tool facilitates the agreement filing process with your home office.
            It does not replace comprehensive succession planning or serve as a substitute for
            independent legal advice. Before executing any agreement, have it reviewed by qualified
            legal counsel with expertise in financial services succession.
          </p>
        </div>
      </Card>
    </SingleColumnTemplate>
  );
};

// --- Protected banner (shown when agreement is approved) ---
const ProtectedBanner = ({ isRj = false, emailSent = false, onEmailCopy, lastDownloadDate }) => (
  <div className="mb-7 relative overflow-hidden rounded-xl border border-succession-success/30 bg-succession-success/5">
    <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-succession-success/5 pointer-events-none" />
    <div className="relative flex gap-4 px-5 py-5">
      <div className="shrink-0 flex items-start pt-0.5">
        <div className="w-11 h-11 rounded-full bg-succession-success/10 border border-succession-success/20 flex items-center justify-center">
          <Shield className="w-5 h-5 text-succession-success" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-succession-text-primary mb-0.5">Your Business is Protected</h3>
        <p className="text-xs text-succession-text-secondary mb-3 leading-relaxed">
          Take these final steps to ensure you've established continuity:
        </p>
        <ul className="space-y-1.5">
          {[
            { text: 'Share plan with key relationships' },
            { text: 'Train staff on plan' },
            { text: 'Communicate plan to clients & prospects' },
            { text: 'Review your plan every year', note: "we'll remind you" },
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-succession-text-secondary">
              <span className="mt-0.5 text-succession-success shrink-0 font-bold">·</span>
              <span>
                {item.text}
                {item.note && <span className="ml-1 text-succession-text-muted italic">({item.note})</span>}
              </span>
            </li>
          ))}
        </ul>

        {isRj && (
          <div className="mt-3.5 pt-3.5 border-t border-succession-success/20 flex items-center justify-between gap-3">
            {emailSent ? (
              <span className="flex items-center gap-1.5 text-xs text-succession-success">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                Sent — check your inbox.
              </span>
            ) : (
              <>
                <span className="text-xs text-succession-text-muted leading-snug">
                  {lastDownloadDate
                    ? `Last requested: ${new Date(lastDownloadDate).toLocaleDateString()}`
                    : 'Get a copy of your approved agreement.'}
                </span>
                <button onClick={onEmailCopy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover transition-theme duration-theme">
                  <Mail className="h-3.5 w-3.5" />
                  Email me a copy
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

// --- Disclosure footer ---
const PactBuilderDisclosure = () => (
  <div className="mt-8 flex gap-2.5 p-3.5 bg-succession-bg-secondary border border-succession-border rounded-lg">
    <AlertCircle className="w-4 h-4 text-succession-text-muted shrink-0 mt-0.5" />
    <p className="text-xs text-succession-text-muted leading-relaxed">
      <strong className="text-succession-text-secondary">Disclosure:</strong> This tool facilitates agreement filing with your home office only and does not
      replace comprehensive succession planning. Any agreement generated or uploaded here should be
      reviewed by independent legal counsel with expertise in financial services succession before
      execution. FindBob does not provide legal, tax, or professional advice.
    </p>
  </div>
);

// --- Main component ---
const PactBuilder = (props) => {
  const {
    current_user,
    continuity_builder: cb,
    user_plan_progress: upp,
    is_rj,
    catastrophic_plans_files_unavailable,
    share_transactional_legal_templates,
    company,
    pact_builder_copy,
  } = props;

  const features = props.features || company?.features || company?.feature_types || [];
  const hasFeature = (featureName) => (
    Array.isArray(features) &&
    features.some((f) => String(f) === featureName || String(f) === `${featureName}_type`)
  );
  const planBuilderEnabled = hasFeature('plan_builder');
  // show_file_your_partner_button gates the manual "register your partner" entry. When OFF,
  // the partner step is a search-only flow (no register button / register copy).
  const searchPartnerOnly = !hasFeature('show_file_your_partner_button');
  const affiliateName = company?.affiliate_name;
  const { isDarkTheme } = useSuccessionTheme();

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [unavailableModalOpen, setUnavailableModalOpen] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [requestCatPlanEmail] = useMutation(REQUEST_CAT_PLAN_EMAIL, { client: apolloClient });
  const subtitleCopy = pact_builder_copy?.subtitle || "File your catastrophic plan agreement at any step — no particular order required.";
  const stepPartnerTitle = pact_builder_copy?.step_partner_title || "Register Your Partner";
  const stepPartnerDescription = pact_builder_copy?.step_partner_description || "Identify and register your catastrophic succession partner so your home office can reach them if needed.";
  const stepFileTitle = pact_builder_copy?.step_file_title || "File Your Agreement";
  const stepFileDescription = pact_builder_copy?.step_file_description || "Print the agreement, review with legal counsel, sign with your partner(s), then upload.";
  const stepAccessTitle = pact_builder_copy?.step_access_title || "Access Your Approved Agreement";
  const stepAccessDescription = pact_builder_copy?.step_access_description || (
    is_rj
      ? "Your approved agreement is stored securely. Request a copy to be emailed to you."
      : "Once approved, download your agreement directly from the Vault."
  );

  // --- Empty state: no ContinuityBuilder created yet ---
  if (!cb) {
    return <PactBuilderEmptyState hasCatPlan={!!upp} planBuilderEnabled={planBuilderEnabled} subtitle={subtitleCopy} searchOnly={searchPartnerOnly} />;
  }

  // --- Derive statuses ---
  const planStatus = (() => {
    if (!upp) return 'not_started';
    if (upp.status === 'completed') return 'complete';
    if (upp.status === 'in_progress') return 'in_progress';
    return 'not_started';
  })();

  const agreementStatus = (() => {
    if (!cb) return 'not_started';
    if (cb.seal_issued || cb.workflow_state === 'seal_issued') return 'approved';
    if (cb.filed_agreement_completed || cb.workflow_state === 'filed_agreement') return 'pending_approval';
    if (cb.workflow_state === 'in_progress') return 'in_progress';
    return 'not_started';
  })();

  // --- Template info ---
  const templateInfo = (() => {
    if (!share_transactional_legal_templates) return null;
    const t = TEMPLATE_BY_AFFILIATE[affiliateName] || TEMPLATE_BY_AFFILIATE.default;
    const affiliateLabel = affiliateName === 'RJA' ? 'RJA Commitment Agreement'
      : affiliateName === 'ICD' ? 'ICD Succession Plan Template'
      : 'Agreement Template';
    return { ...t, affiliateLabel };
  })();

  // --- Handle "Email me a copy" ---
  const handleEmailMeCopy = () => {
    if (catastrophic_plans_files_unavailable) {
      setUnavailableModalOpen(true);
      return;
    }
    requestCatPlanEmail()
      .then(({ data }) => {
        const result = data?.request_cat_plan_email;
        if (result?.success) setEmailSent(true);
        else if (result?.message === 'catastrophic_plans_files_unavailable') setUnavailableModalOpen(true);
      })
      .catch(() => setEmailSent(true));
  };

  // --- Plan completion label ---
  const planLabel = (() => {
    if (planStatus === 'complete') return 'Plan complete';
    if (planStatus === 'in_progress') return `${upp?.completion_percentage || 0}${pctSymbol()} complete`;
    return null;
  })();

  const partnerComplete = cb?.partner_step_completed || (cb?.partners?.length || 0) > 0 || !!cb?.existing_partner_timestamp;

  // --- Overall progress (for summary bar) ---
  const progressSteps = planBuilderEnabled
    ? [
      planStatus !== 'not_started',
      partnerComplete,
      agreementStatus !== 'not_started',
      agreementStatus === 'approved',
    ]
    : [
      partnerComplete,
      agreementStatus !== 'not_started',
      agreementStatus === 'approved',
    ];
  const totalSteps = progressSteps.length;
  const completedCount = progressSteps.filter(Boolean).length;

  const step3Status = agreementStatus === 'not_started' || agreementStatus === 'in_progress'
    ? 'not_started'
    : agreementStatus === 'pending_approval'
      ? 'filed'
      : 'complete';

  return (
    <SingleColumnTemplate
      title="Your Catastrophic Agreement"
      subtitle={subtitleCopy}
      columnClassName="max-w-2xl"
    >

      {/* Progress summary / protected banner */}
      {agreementStatus === 'approved' ? (
        <ProtectedBanner
          isRj={is_rj}
          emailSent={emailSent}
          onEmailCopy={handleEmailMeCopy}
          lastDownloadDate={cb?.last_cat_plan_downloaded_date}
        />
      ) : (
        <div className="mb-7 px-5 py-4 bg-succession-bg-primary border border-succession-border rounded-xl shadow-succession-sm">
          <div className="flex items-center justify-between mb-2.5">
            <span className="text-xs font-medium text-succession-text-secondary">Overall Progress</span>
            <span className="text-xs font-semibold text-succession-text-primary">{completedCount} of {totalSteps} steps complete</span>
          </div>
          <div className="flex gap-1.5">
            {progressSteps.map((done, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${done ? 'bg-succession-success' : 'bg-succession-border'}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Steps — timeline */}
      <div>

        {planBuilderEnabled && (
          <StepCard
            number={1}
            title="Create Your Plan"
            description="Use the guided plan builder to create your catastrophic succession plan, or download a blank template to complete on your own."
            status={planStatus}
            isLast={false}
          >
            <Step1PlanAction
              planStatus={planStatus}
              planLabel={planLabel}
              templateInfo={templateInfo}
              upp={upp}
            />
          </StepCard>
        )}

        {/* Step 2 — Partnering */}
        {(() => {
          return (
            <StepCard
              number={planBuilderEnabled ? 2 : 1}
              title={stepPartnerTitle}
              description={stepPartnerDescription}
              status={partnerComplete ? 'complete' : 'not_started'}
              isLast={false}
              isOptional={true}
            >
              <Step2FindPartner cb={cb} brandedName={company?.branded_name} searchOnly={searchPartnerOnly} />
            </StepCard>
          );
        })()}

        {/* Step 3 — File Agreement */}
        <StepCard
          number={planBuilderEnabled ? 3 : 2}
          title={stepFileTitle}
          description={stepFileDescription}
          status={step3Status}
          isLast={false}
        >
          <Step3FileAgreement
            agreementStatus={agreementStatus}
            onUpload={() => setUploadModalOpen(true)}
            templateInfo={templateInfo}
          />
        </StepCard>

        {/* Step 4 — Approved */}
        <StepCard
          number={planBuilderEnabled ? 4 : 3}
          title={stepAccessTitle}
          description={stepAccessDescription}
          status={agreementStatus === 'approved' ? 'approved' : 'not_started'}
          isLast={true}
        >
          {agreementStatus === 'approved' ? (
            <div className="space-y-2">
              {is_rj ? (
                <div>
                  {emailSent ? (
                    <p className="text-xs text-succession-success font-medium">
                      Your agreement is being retrieved and will be emailed to you.
                    </p>
                  ) : (
                    <button
                      onClick={handleEmailMeCopy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover transition-theme duration-theme"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      Email me a copy
                    </button>
                  )}
                  {cb?.last_cat_plan_downloaded_date && (
                    <p className="text-xs text-succession-text-muted mt-1.5">
                      Last requested: {new Date(cb.last_cat_plan_downloaded_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ) : (
                <a
                  href="/succession/vault"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-succession-border text-succession-text-primary bg-succession-bg-secondary hover:bg-succession-hover-bg transition-theme duration-theme"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download Agreement
                </a>
              )}
            </div>
          ) : (
            <p className="text-xs text-succession-text-muted">
              Your agreement will appear here once it has been reviewed and approved.
            </p>
          )}
        </StepCard>

      </div>

      {/* Upload modal — needs react-apollo ApolloProvider + SnackbarProvider for its internal hooks */}
      {uploadModalOpen && (
        <ApolloProvider client={apolloClient}>
          <SnackbarProvider maxSnack={3}>
          <DocumentUploadModal
            open={uploadModalOpen}
            onClose={() => setUploadModalOpen(false)}
            onSuccess={() => {
              setUploadModalOpen(false);
              window.location.reload();
            }}
            defaultTitle="Catastrophic Plan Agreement"
            defaultCategory="legal"
            defaultSourceType="plan_builder_catastrophic"
            templateInfo={templateInfo}
          />
          </SnackbarProvider>
        </ApolloProvider>
      )}

      {/* Disclosure */}
      <PactBuilderDisclosure />

      {/* Unavailable modal */}
      {unavailableModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-succession-bg-primary rounded-lg border border-succession-border shadow-succession-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-sm font-semibold text-succession-text-primary mb-2">Agreement Temporarily Unavailable</h3>
            <p className="text-xs text-succession-text-secondary mb-4">
              Your approved agreement is temporarily unavailable for download. Please contact your branch administrator for assistance.
            </p>
            <button
              onClick={() => setUnavailableModalOpen(false)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-succession-border text-succession-text-primary bg-succession-bg-secondary hover:bg-succession-hover-bg transition-theme duration-theme"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </SingleColumnTemplate>
  );
};

export default PactBuilder;
